import React, {
  useMemo,
  useContext,
  createContext,
  ComponentProps,
  HTMLAttributes,
  MouseEventHandler
} from 'react';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { type LucideIcon, XIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';

export const bannerVariants = cva(
  'tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-rounded-xl tw-px-4 tw-py-2',
  {
    variants: {
      variant: {
        primary: 'tw-bg-primary tw-text-primary-foreground',
        secondary: 'tw-bg-secondary tw-text-secondary-foreground',
        warning: 'tw-bg-warning tw-text-warning-foreground',
        success: 'tw-bg-positive tw-text-positive-foreground',
        destructive: 'tw-bg-destructive tw-text-destructive-foreground'
      }
    },
    defaultVariants: {
      variant: 'primary'
    }
  }
);

type BannerContextProps = {
  show: boolean;
  setShow: (show: boolean) => void;
};

export const BannerContext = createContext<BannerContextProps>({
  show: true,
  setShow: () => undefined
});

export type BannerProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof bannerVariants> & {
    visible?: boolean;
    defaultVisible?: boolean;
    onClose?: () => void;
  };

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  ({ children, visible, defaultVisible = true, onClose, className, variant, ...props }, ref) => {
    const [show, setShow] = useControllableState({
      defaultProp: defaultVisible,
      prop: visible,
      onChange: (newShowValue: boolean) => {
        if (!newShowValue) {
          onClose?.();
        }
      }
    });

    const ctxValue = useMemo(() => ({ show, setShow }), [show, setShow]);

    if (!show) {
      return null;
    }

    return (
      <BannerContext.Provider value={ctxValue}>
        <div ref={ref} {...props} className={cn(bannerVariants({ variant }), className)}>
          {children}
        </div>
      </BannerContext.Provider>
    );
  }
);

export type BannerIconProps = HTMLAttributes<HTMLDivElement> & {
  icon: LucideIcon;
};

export const BannerIcon: React.FC<BannerIconProps> = ({ icon: Icon, className, ...props }) => (
  <div className={cn('tw-shadow-sm', className)} {...props}>
    <Icon size={16} />
  </div>
);

export type BannerTitleProps = HTMLAttributes<HTMLParagraphElement>;

export const BannerTitle: React.FC<BannerTitleProps> = ({ className, ...props }) => (
  <p
    className={cn('tw-m-0 tw-flex-1 tw-text-sm tw-font-normal tw-leading-5', className)}
    {...props}
  />
);

export type BannerCloseProps = ComponentProps<typeof Button>;

export const BannerClose: React.FC<BannerCloseProps> = ({
  variant = 'ghost',
  size = 'none',
  onClick,
  className,
  ...props
}: BannerCloseProps) => {
  const { setShow } = useContext(BannerContext);

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    setShow(false);
    onClick?.(e);
  };

  return (
    <Button
      onClick={handleClick}
      size={size}
      aria-label="Close banner"
      variant={variant}
      {...props}
      className={cn(
        'tw-shrink-0 tw-bg-transparent tw-text-inherit hover:tw-bg-background/10',
        className
      )}
    >
      <XIcon size={16} />
    </Button>
  );
};
